import { forwardRef } from "react";
import "./PlanCard.css";

export type PlanVariant = "monthly" | "quarterly";

export interface PlanFeature {
  text: string;
  emphasized?: boolean;
}

export interface PlanCardProps {
  variant: PlanVariant;
  pageCode: string;
  title: string;
  price: string;
  priceSubtext: string;
  features: PlanFeature[];
  ctaLabel: string;
  ctaAriaLabel: string;
  badge?: string;
  onSelect: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  id?: string;
}

export const PlanCard = forwardRef<HTMLDivElement, PlanCardProps>(function PlanCard(
  {
    variant,
    pageCode,
    title,
    price,
    priceSubtext,
    features,
    ctaLabel,
    ctaAriaLabel,
    badge,
    onSelect,
    isLoading = false,
    isDisabled = false,
    errorMessage,
    id,
  },
  ref,
) {
  const titleId = `plan-${variant}-title`;
  const buttonLabel = isLoading ? "PLEASE WAIT" : ctaLabel;
  const buttonDisabled = isLoading || isDisabled;

  return (
    <div
      ref={ref}
      id={id}
      className={`plan-card-wrap plan-card-wrap--${variant}`}
    >
      {badge && (
        <span
          className={`plan-card__badge plan-card__badge--${variant}`}
        >
          {badge}
        </span>
      )}
      <section
        aria-labelledby={titleId}
        className={`plan-card plan-card--${variant} ${
          errorMessage ? "plan-card--error" : ""
        }`}
        data-recommended={variant === "quarterly" ? "true" : undefined}
      >
        <h2 id={titleId} className="plan-card__title">
          {title}
        </h2>

        <p className="plan-card__price-row">
          <span className="plan-card__price crt-glow">{price}</span>
        </p>
        <p className="plan-card__price-subtext">{priceSubtext}</p>

        <hr className="plan-card__divider" aria-hidden="true" />

        <ul className="plan-card__features">
          {features.map((f) => (
            <li key={f.text} className="plan-card__feature">
              <span className="plan-card__marker" aria-hidden="true" />
              <span
                className={
                  f.emphasized
                    ? "plan-card__feature-text plan-card__feature-text--emphasized"
                    : "plan-card__feature-text"
                }
              >
                {f.text}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="plan-card__cta"
          onClick={onSelect}
          disabled={buttonDisabled}
          aria-label={ctaAriaLabel}
          aria-busy={isLoading}
        >
          {buttonLabel}
        </button>

        {errorMessage && (
          <p className="plan-card__error" role="alert">
            {errorMessage}
          </p>
        )}
      </section>
      <span className="plan-card__page-code" aria-hidden="true">
        {pageCode}
      </span>
    </div>
  );
});
